// PageTaskbar.jsx — the Win98 taskbar for standalone pages (forms, legal,
// the LP portal). Fixed to the bottom like the desktop taskbar: Start opens
// the same Start menu the desktop shows (picking a program hands the launch
// off to the desktop via sessionStorage, so e.g. the LP portal stays open
// until the user actually picks somewhere to go), one sunken button names
// the "window" you're in, and the tray keeps the social links and the clock.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OSIcon32 from "../../icons/OSIcon32";
import { START_APPS, SOCIAL_LINKS, DESKTOP_LAUNCH_KEY } from "../../../data/desktopPrograms";
import "../../../styles/App.css";

const Glyph = ({ path, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d={path} />
  </svg>
);

const TrayLink = ({ href, title, path }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    aria-label={title}
    style={{ display: "flex", alignItems: "center", color: "var(--chrome-outline)" }}
  >
    <Glyph path={path} />
  </a>
);

export default function PageTaskbar({ windowLabel, windowIcon = "navigator" }) {
  const navigate = useNavigate();
  const [clock, setClock] = useState({ time: "", date: "", dateCompact: "" });
  const [startOpen, setStartOpen] = useState(false);
  const startRef = useRef(null);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const dateStr = now.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      setClock({
        time: timeStr,
        // hardcoded year — part of the Y2K-era aesthetic; CSS picks the
        // full or compact form by viewport width
        date: `${dateStr} 2003`,
        dateCompact: `${now.getMonth() + 1}/${now.getDate()}/03`,
      });
    };
    updateClock();
    const interval = setInterval(updateClock, 1000 * 60);
    return () => clearInterval(interval);
  }, []);

  // Close the Start menu on outside click or Escape — same behavior as the
  // desktop taskbar's menu.
  useEffect(() => {
    if (!startOpen) return;
    const onPointerDown = (e) => {
      if (startRef.current && !startRef.current.contains(e.target)) {
        setStartOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setStartOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [startOpen]);

  // Launch a program from a standalone page: the desktop owns the windows,
  // so stash the request and navigate there. The portal route is its own
  // page and navigates directly. Marking the session booted skips the boot
  // screen on the way back.
  const launchFromPage = (appId) => {
    setStartOpen(false);
    try {
      sessionStorage.setItem("gnf-has-booted", "1");
    } catch {}
    if (appId === "lpPortal") {
      navigate("/portal");
      return;
    }
    try {
      sessionStorage.setItem(DESKTOP_LAUNCH_KEY, appId);
    } catch {}
    navigate("/");
  };

  const handleShowDesktop = () => {
    setStartOpen(false);
    try {
      sessionStorage.setItem("gnf-has-booted", "1");
    } catch {}
    navigate("/");
  };

  return (
    <div className="win95-taskbar-98" ref={startRef}>
      <button
        type="button"
        className="win95-taskbar-98__start"
        onClick={() => setStartOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={startOpen}
      >
        <img src="/assets/gnf-logo.png" alt="" width="16" height="16" style={{ display: "block" }} />
        Start
      </button>

      {startOpen && (
        <div className="win95-startmenu" role="menu" aria-label="Start menu">
          <div className="win95-startmenu__banner" aria-hidden="true">
            <span>NeighborhoodOS</span>
          </div>
          <div className="win95-startmenu__items">
            {START_APPS.map((app) => (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => launchFromPage(app.id)}
              >
                <OSIcon32 kind={app.icon} size={24} />
                {app.label}
              </button>
            ))}
            <div className="win95-startmenu__divider" aria-hidden="true" />
            <button
              type="button"
              role="menuitem"
              className="win95-startmenu__item"
              onClick={handleShowDesktop}
            >
              <OSIcon32 kind="computer" size={24} />
              Desktop
            </button>
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.label}
                role="menuitem"
                className="win95-startmenu__item"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setStartOpen(false)}
              >
                <Glyph path={link.path} size={16} />
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="win95-toolbar__sep" aria-hidden="true" />

      {windowLabel && (
        <button type="button" className="win95-taskbar-btn is-active" aria-current="page">
          <OSIcon32 kind={windowIcon} size={16} style={{ flex: "none" }} />
          <span className="win95-taskbar-btn__label">{windowLabel}</span>
        </button>
      )}

      <div style={{ flex: 1 }} />

      <div className="win95-taskbar-98__tray">
        {SOCIAL_LINKS.map((link) => (
          <TrayLink key={link.label} href={link.href} title={link.label} path={link.path} />
        ))}
        <div className="win95-taskbar-98__tray-divider" aria-hidden="true" />
        <span className="retro-clock">
          {clock.time}
          <span className="retro-clock__date"> · {clock.date}</span>
          <span className="retro-clock__date--compact"> · {clock.dateCompact}</span>
        </span>
      </div>
    </div>
  );
}
